'use client';

import { c as _c } from "react/compiler-runtime";
import { uiTestId } from '@7f-ui/test-ids';
import { type IconProps, WarningOctagon } from '@phosphor-icons/react';
import type { ComponentType, ElementType, PropsWithChildren, ReactNode } from 'react';
import { AppLinkLoadingButton } from 'src/app/_page-chrome/link-loading-button';
import { BaseButton, type ButtonProps } from '../../../@7f-ui/ui-components/src/lib/components/button/button';
import { Skeleton } from '../../../@7f-ui/ui-components/src/lib/components/skeleton/skeleton';
import { cx } from '../../../@7f-ui/ui-components/src/lib/cx';
import type { ButtonAsProps } from '../../../@7f-ui/ui-components/src/lib/polymorphic-props';
function AppBookingBoxRoot(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex flex-col gap-6 rounded-xl px-5 py-8 shadow">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
type AppBookingBoxTitleProps = PropsWithChildren<{
  isLoading?: boolean;
}>;
function AppBookingBoxTitle(t0) {
  const $ = _c(4);
  const {
    children,
    isLoading
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Skeleton className="h-8" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = uiTestId("BookingBox.title");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <div className="grid gap-2" data-testid={t1}>{children}</div>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
type AppBookingBoxFieldProps = PropsWithChildren<{
  label: ReactNode;
}>;
function AppBookingBoxField(t0) {
  const $ = _c(5);
  const {
    children,
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="font-heading5">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className="flex flex-col gap-2">{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
type AppBookingBoxButtonProps = PropsWithChildren<{
  Icon: ComponentType<IconProps>;
  isError?: boolean;
  message?: string;
} & ButtonAsProps<'button'>>;
function AppBookingBoxButton(t0) {
  const $ = _c(29);
  let Icon;
  let children;
  let isError;
  let message;
  let rest;
  if ($[0] !== t0) {
    ({
      Icon,
      children,
      isError,
      message,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = children;
    $[3] = isError;
    $[4] = message;
    $[5] = rest;
  } else {
    Icon = $[1];
    children = $[2];
    isError = $[3];
    message = $[4];
    rest = $[5];
  }
  const t1 = isError && "border-error";
  let t2;
  if ($[6] !== t1) {
    t2 = cx("flex items-center gap-2 rounded-xl border border-primary/15 p-4 font-body-copy", t1);
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== Icon) {
    t3 = <Icon className="size-6 shrink-0" />;
    $[8] = Icon;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = isError && "text-error";
  let t5;
  if ($[10] !== t4) {
    t5 = cx("block min-w-0 grow truncate text-start", t4);
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== children || $[13] !== t5) {
    t6 = <span className={t5}>{children}</span>;
    $[12] = children;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== isError) {
    t7 = isError && <WarningOctagon weight="bold" className="size-6 shrink-0 justify-self-end text-error" />;
    $[15] = isError;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== rest || $[18] !== t2 || $[19] !== t3 || $[20] !== t6 || $[21] !== t7) {
    t8 = <BaseButton className={t2} {...rest}>{t3}{t6}{t7}</BaseButton>;
    $[17] = rest;
    $[18] = t2;
    $[19] = t3;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== isError || $[24] !== message) {
    t9 = message && <span className={cx("pl-3 font-base-caption text-secondary-text", isError && "text-error")} data-testid={uiTestId("BookingBox.message")}>{message}</span>;
    $[23] = isError;
    $[24] = message;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t8 || $[27] !== t9) {
    t10 = <div className="flex flex-col justify-center gap-2">{t8}{t9}</div>;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
function AppBookingBoxCTA(t0) {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <AppLinkLoadingButton {...rest}>{children}</AppLinkLoadingButton>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export const AppBookingBox = Object.assign(AppBookingBoxRoot, {
  Title: AppBookingBoxTitle,
  Field: AppBookingBoxField,
  Button: AppBookingBoxButton,
  CTA: AppBookingBoxCTA
});